/**
 * ActivityFeed - displays recent activity in the organization
 */

import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from '@/i18n/dateFns';
import type { ActivityItem } from '@services/api';

interface ActivityFeedProps {
  activities: ActivityItem[];
  isLoading?: boolean;
}

const activityIcons: Record<ActivityItem['type'], { icon: string; color: string }> = {
  task_created: { icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6', color: 'bg-green-500' },
  task_updated: { icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z', color: 'bg-blue-500' },
  task_completed: { icon: 'M5 13l4 4L19 7', color: 'bg-green-600' },
  task_moved: { icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4', color: 'bg-purple-500' },
  comment_added: { icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', color: 'bg-neutral-500' },
  meeting_created: { icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', color: 'bg-indigo-500' },
  meeting_completed: { icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'bg-indigo-600' },
};

export function ActivityFeed({ activities, isLoading }: ActivityFeedProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">{t('dashboard.recentActivity')}</h3>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-start gap-3 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-neutral-200 dark:bg-neutral-700" />
              <div className="flex-1">
                <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4 mb-2" />
                <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">{t('dashboard.recentActivity')}</h3>
      {activities.length === 0 ? (
        <p className="text-neutral-500 dark:text-neutral-400 text-sm">{t('dashboard.noRecentActivity')}</p>
      ) : (
        <div className="flow-root">
          <ul className="-mb-8">
            {activities.map((activity, idx) => {
              const iconInfo = activityIcons[activity.type];
              const isLast = idx === activities.length - 1;

              return (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {!isLast && (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-neutral-200 dark:bg-neutral-700"
                        aria-hidden="true"
                      />
                    )}
                    <div className="relative flex space-x-3">
                      <div>
                        <span
                          className={`h-8 w-8 rounded-full ${iconInfo.color} flex items-center justify-center ring-8 ring-white dark:ring-neutral-800`}
                        >
                          <svg
                            className="h-4 w-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d={iconInfo.icon}
                            />
                          </svg>
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                        <div>
                          <p className="text-sm text-neutral-800 dark:text-neutral-200">
                            <span className="font-medium">{activity.userName}</span>{' '}
                            {activity.description}{' '}
                            <span className="font-medium text-primary-600 dark:text-primary-400">
                              {activity.entityTitle}
                            </span>
                          </p>
                          {activity.projectName && (
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                              {t('dashboard.inActivity', { project: activity.projectName })}
                            </p>
                          )}
                        </div>
                        <div className="whitespace-nowrap text-right text-xs text-neutral-500 dark:text-neutral-400">
                          {formatDistanceToNow(new Date(activity.createdAt), {
                            addSuffix: true,
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
